import { useCallback, useEffect, useState } from 'react'
import { AdManagementService } from '../../client'
import { AVAILABLE_COLUMNS, type ColumnDef } from '../../lib/adColumns'
import { apiErrorMessage } from '../../lib/api'
import { formatCurrency, formatDate, formatValue } from '../../lib/format'
import AdDetailDialog, { type AdRow } from './AdDetailDialog'
import AdsTableSkeleton from './AdsTableSkeleton'

const PAGE_SIZE = 10
const HEAD = 'px-4 py-3 text-left text-xs font-medium whitespace-nowrap text-gray-500 uppercase'

function metric(insights: Record<string, unknown> | undefined, key: string, format: ColumnDef['format']) {
  const value = insights?.[key]

  if (Array.isArray(value)) {
    const total = value.reduce((sum, item) => sum + Number((item as { value?: unknown }).value ?? 0), 0)
    return format === 'currency' ? formatCurrency(total) : total.toLocaleString()
  }

  return formatValue(value, format)
}

function statusClass(status?: string) {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'font-medium text-green-600'
    case 'PAUSED':
      return 'font-medium text-yellow-600'
    case 'ARCHIVED':
      return 'font-medium text-gray-500'
    default:
      return 'text-gray-500'
  }
}

function Cell({ ad, column }: { ad: AdRow; column: ColumnDef }) {
  switch (column.key) {
    case 'campaign':
      return ad.campaign?.name ? (
        <div className="min-w-0">
          <p className="truncate">{ad.campaign.name}</p>
          <p className="text-xs text-gray-400">{ad.campaign.objective?.replace('OUTCOME_', '')}</p>
        </div>
      ) : (
        <span className="text-gray-400">—</span>
      )
    case 'adset':
      return ad.adset?.name ? <p className="truncate">{ad.adset.name}</p> : <span className="text-gray-400">—</span>
    case 'status':
      return (
        <span className={statusClass(ad.status)}>
          {ad.status ? ad.status.charAt(0) + ad.status.slice(1).toLowerCase() : '—'}
        </span>
      )
    case 'created_at':
      return formatDate(ad.published_at ?? ad.created_time)
    default:
      return metric(ad.insights, column.key, column.format)
  }
}

type Props = {
  brandId: number
  adAccountId?: string | null
  campaignId?: string
  adsetId?: string
  adlovinOnly?: boolean
  columns: string[]
}

export default function AdsTable({ brandId, adAccountId, campaignId, adsetId, adlovinOnly, columns }: Props) {
  const [ads, setAds] = useState<AdRow[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [updating, setUpdating] = useState<string[]>([])
  const [selected, setSelected] = useState<AdRow | null>(null)
  const [error, setError] = useState('')

  const activeColumns = AVAILABLE_COLUMNS.filter(({ key }) => columns.includes(key))
  const rows = adlovinOnly ? ads.filter((ad) => ad.is_adlovin_created) : ads
  const filtered = Boolean(campaignId || adsetId || adlovinOnly)

  const fetchPage = useCallback(
    async (pageCursor?: string) => {
      const page = await AdManagementService.getAds(
        brandId,
        campaignId || null,
        adsetId || null,
        pageCursor ?? null,
        PAGE_SIZE,
      )
      return { ads: (page.ads ?? []) as AdRow[], cursor: page.next_cursor ?? null }
    },
    [brandId, campaignId, adsetId],
  )

  useEffect(() => {
    fetchPage()
      .then((page) => {
        setAds(page.ads)
        setCursor(page.cursor)
      })
      .catch((err: unknown) => setError(apiErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [fetchPage])

  async function loadMore() {
    if (!cursor || loadingMore) return

    setLoadingMore(true)
    try {
      const page = await fetchPage(cursor)
      setAds((current) => [...current, ...page.ads])
      setCursor(page.cursor)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoadingMore(false)
    }
  }

  async function toggleStatus(ad: AdRow) {
    const next = ad.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'
    const previous = ad.status

    setAds((current) => current.map((item) => (item.ad_id === ad.ad_id ? { ...item, status: next } : item)))
    setUpdating((current) => [...current, ad.ad_id])

    try {
      await AdManagementService.updateAdStatus(String(ad.ad_id), brandId, { status: next })
    } catch (err) {
      setAds((current) => current.map((item) => (item.ad_id === ad.ad_id ? { ...item, status: previous } : item)))
      setError(apiErrorMessage(err))
    } finally {
      setUpdating((current) => current.filter((id) => id !== ad.ad_id))
    }
  }

  if (loading) return <AdsTableSkeleton />

  return (
    <div>
      {rows.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-500">
            {adlovinOnly
              ? 'No Adlovin-created ads found. Load more to continue searching.'
              : filtered
                ? 'No ads match the selected filter.'
                : 'No ads found for this account.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto [scrollbar-width:thin]">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col style={{ width: '240px' }} />
              <col style={{ width: '72px' }} />
              {activeColumns.map(({ key, width }) => (
                <col key={key} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className={HEAD}>Ad</th>
                <th className={HEAD}>Active</th>
                {activeColumns.map(({ key, label }) => (
                  <th key={key} className={HEAD}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {rows.map((ad) => {
                const archived = ad.status === 'ARCHIVED' || ad.status === 'DELETED'
                const active = ad.status === 'ACTIVE'

                return (
                  <tr key={ad.ad_id} onClick={() => setSelected(ad)} className="cursor-pointer hover:bg-stone-200">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-3">
                        {ad.thumbnail_url ? (
                          <img src={ad.thumbnail_url} alt="" className="size-8 rounded object-cover" />
                        ) : (
                          <div className="size-8 rounded bg-gray-200" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium">{ad.ad_name || `Ad #${ad.ad_id}`}</p>
                          {ad.is_adlovin_created && <p className="truncate text-xs text-gray-400">Adlovin</p>}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={active}
                        aria-label={active ? 'Pause ad' : 'Activate ad'}
                        disabled={archived || updating.includes(ad.ad_id)}
                        onClick={() => void toggleStatus(ad)}
                        className={`relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-stone-100 focus:outline-none ${
                          active ? 'bg-indigo-600' : 'bg-gray-300'
                        } ${archived ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            active ? 'translate-x-3' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>

                    {activeColumns.map((column) => (
                      <td key={column.key} className="px-4 py-3 text-sm whitespace-nowrap text-gray-600">
                        <Cell ad={ad} column={column} />
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {error && <p className="pt-3 text-center text-xs text-red-500">{error}</p>}

      {cursor && (
        <div className="flex flex-col items-center gap-1 pt-4">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => void loadMore()}
            className="rounded-md border border-gray-300 bg-stone-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-stone-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {selected && (
        <AdDetailDialog
          ad={selected}
          brandId={brandId}
          adAccountId={adAccountId}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
